import { useState, type FormEvent } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { useAuth } from '@/auth/useAuth'
import { ApiError } from '@/api/client'
import { SeaBackdrop } from '@/components/SeaBackdrop'
import { Field } from '@/pages/Login'
import { SeoHead } from '@/components/SeoHead'

/**
 * /register — "Nouveau capitaine" sign-up over the shared <SeaBackdrop/>.
 * Validation/submit logic is unchanged from the original; presentation is new.
 */
export function Register() {
  const { register, isAuthenticated, isLoading } = useAuth()
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [pseudo, setPseudo] = useState('')
  const [password, setPassword] = useState('')
  const [errors, setErrors] = useState<Record<string, string[]>>({})
  const [generalError, setGeneralError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  if (isLoading) return null
  if (isAuthenticated) return <Navigate to="/" replace />

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setErrors({})
    setGeneralError(null)
    setSubmitting(true)
    try {
      await register({ email, pseudo, password })
      navigate('/', { replace: true })
    } catch (err) {
      if (err instanceof ApiError && err.errors) {
        setErrors(err.errors)
      } else if (err instanceof ApiError) {
        setGeneralError(err.message || 'Inscription échouée')
      } else {
        setGeneralError('Inscription échouée')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const fieldError = (field: string) => errors[field]?.[0]

  return (
    <section className="relative min-h-[calc(100vh-8rem)] overflow-hidden flex items-start lg:items-center justify-center px-6 pt-10 sm:pt-16 lg:pt-0">
      <SeoHead title="Créer un compte capitaine · Corsalia" noindex />
      <SeaBackdrop shipAlign="side" />

      <div className="relative z-10 w-full max-w-md">
        <div className="rounded-2xl border border-sand-500/25 bg-ocean-900/70 backdrop-blur-md shadow-[0_30px_70px_-30px_rgba(0,0,0,0.8)] p-7 sm:p-8">
          <div className="text-center mb-6">
            <h1 className="font-display text-3xl text-sand-300">Nouveau capitaine</h1>
            <p className="text-sm text-ocean-200 mt-1">Tu rejoins l'archipel. Choisis ton nom avec soin.</p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <Field
              id="email"
              label="Email"
              type="email"
              value={email}
              onChange={setEmail}
              autoComplete="email"
              autoFocus
              required
              error={fieldError('email')}
            />
            <Field
              id="pseudo"
              label="Pseudo de capitaine"
              value={pseudo}
              onChange={setPseudo}
              autoComplete="username"
              placeholder="ex : CaptainJack, BarbeNoire_42"
              minLength={3}
              maxLength={30}
              required
              hint="3–30 caractères, lettres/chiffres/tirets/underscores. Visible publiquement."
              error={fieldError('pseudo')}
            />
            <Field
              id="password"
              label="Mot de passe"
              type="password"
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
              minLength={8}
              required
              hint="8 caractères minimum."
              error={fieldError('password')}
            />

            {generalError && (
              <div className="text-rum-500 text-sm bg-rum-700/20 border border-rum-500/30 rounded-lg p-3">
                {generalError}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full mt-2 px-4 py-3 rounded-lg bg-gradient-to-b from-sand-300 to-sand-500 text-ocean-900 font-display font-bold text-lg ring-1 ring-sand-100/50
                         hover:-translate-y-0.5 transition-transform disabled:opacity-50 disabled:cursor-not-allowed disabled:translate-y-0"
            >
              {submitting ? 'Création…' : "Lever l'ancre"}
            </button>
          </form>

          <p className="text-center text-sm text-ocean-200 mt-6">
            Déjà un compte ?{' '}
            <Link to="/login" className="text-sand-300 hover:text-sand-100 underline underline-offset-4">
              Se connecter
            </Link>
          </p>
        </div>
      </div>
    </section>
  )
}
